/**
 * ManagementDataContext.js
 *
 * Single source of truth for the Management module's live business data. It
 * background-polls orders right after the management module opens so that:
 *   - the statistics/dashboard tab stays fresh without a per-screen fetch, and
 *   - the orders list renders instantly (no spinner) and keeps updating.
 *
 * Polling strategy (see plan + clarified product decisions):
 *   - FAST tick (user-selectable cadence, default 5s): re-fetch ONLY the orders
 *     endpoint — one cheap call that drives every KPI/chart and the orders list.
 *   - SLOW tick (max(interval, 60s)): re-fetch the full dashboard aggregate so
 *     the heavier "business-area" datasets (stock, CRM, invoices, work-orders…)
 *     refresh without hammering ~17 endpoints every few seconds.
 *   - Polling runs continuously while the management module is mounted and is
 *     paused while the app is backgrounded (mirrors OrdersContext.js discipline),
 *     resuming with an immediate fetch on foreground.
 *
 * The cadence is user-configurable (5s / 15s / 30s / 1m / 5m / Never) and is
 * persisted via persistentSettings under MANAGEMENT_REFRESH_INTERVAL_KEY.
 *
 * The provider is mounted (gated to manager mode) around the whole management
 * navigator in app/components/AppNavigator.jsx, so it wraps both the dashboard
 * tabs (ManagementHomeScreen) and the sibling ManagementListScreen/DetailScreen.
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppState} from 'react-native';
import {
    MANAGEMENT_REFRESH_DEFAULT_MS,
    MANAGEMENT_REFRESH_INTERVAL_KEY,
    fetchManagementDashboardData,
    fetchManagementOrders,
    getManagementDateRange,
    getManagementScope,
    sanitizeRefreshIntervalMs,
} from './managementApi';
import {getSetting, setSetting} from './persistentSettings';

/** Empty dashboard aggregate (same shape ManagementDashboardScreen expects). */
const EMPTY_DATA = {
    orders: [], foods: [], stock: [], leads: [], crmStats: null, customers: [], quotes: [],
    workOrders: [], technicians: [], appointments: [],
    invoicesInbound: [], invoicesOutbound: [], banks: [], registers: [], costTypes: [],
    warehouses: [], transactions: [],
};

/** The heavy aggregate never refreshes faster than this, regardless of cadence. */
const SECONDARY_MIN_MS = 60 * 1000;

/**
 * Above this range (in days) the order list is large enough (months / thousands
 * of orders) that re-fetching + diffing the ENTIRE list every few seconds is pure
 * churn with no live-feel benefit — nobody watches a 3-month report tick. For such
 * ranges we skip the FAST orders-only tick entirely and let the SLOW full-aggregate
 * tick (>=60s) keep the data fresh. Small/"today"-ish ranges keep the live cadence.
 * Configurable here rather than hardcode-disabling polling.
 */
const FAST_POLL_MAX_RANGE_DAYS = 7;

/**
 * Inclusive whole-day count between two dates (min 1). Lets a custom calendar
 * range still feed a sane day count to day-count-dependent chart builders.
 * @param {Date|string|number} start range start.
 * @param {Date|string|number} end range end.
 * @returns {number}
 */
const dayCountFromRange = (start, end) => {
    const a = new Date(start).getTime();
    const b = new Date(end).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return 30;
    return Math.max(1, Math.round(Math.abs(b - a) / (24 * 60 * 60 * 1000)) + 1);
};

/**
 * Cheap structural signature of an orders array. Lets us skip a setState (and the
 * app-wide re-render it triggers through the context value) when a poll returned
 * data identical to what we already hold. Mirrors OrdersContext.commitOrders, but
 * avoids a full JSON.stringify of 2000 objects on every 5s tick: length + each
 * order's _id/updatedAt/orderSum/status is enough to detect any change that
 * affects a KPI, chart or list row.
 */
const ordersSignature = (list) => {
    if (!Array.isArray(list) || list.length === 0) return `${Array.isArray(list) ? 0 : 'x'}`;
    let sig = `${list.length}`;
    for (let i = 0; i < list.length; i += 1) {
        const o = list[i] || {};
        sig += `|${o._id || o.id || ''}:${o.updatedAt || ''}:${o.orderSum || ''}:${o.status || ''}`;
    }
    return sig;
};

/** Inert value returned by the hook when no provider is mounted (non-manager modes). */
const INERT_VALUE = {
    scope: null,
    days: 30,
    setDays: () => {},
    range: null,
    setRange: () => {},
    orders: [],
    data: EMPTY_DATA,
    loading: false,
    refreshing: false,
    error: '',
    lastUpdatedAt: null,
    intervalMs: MANAGEMENT_REFRESH_DEFAULT_MS,
    setIntervalMs: () => {},
    refresh: () => {},
};

const ManagementDataContext = createContext(null);

/**
 * Access the live management data context. Safe to call outside the provider —
 * returns inert defaults (so screens reachable in other app modes don't crash).
 * @returns {typeof INERT_VALUE & {data: object}}
 */
export const useManagementData = () => useContext(ManagementDataContext) || INERT_VALUE;

/**
 * Provider that owns and background-polls the management module's live data.
 * @param {{children: React.ReactNode}} props
 */
export const ManagementDataProvider = ({children}) => {
    const [scope, setScope] = useState(null);
    const [days, setDaysState] = useState(30);
    // Custom analytics window (from the calendar DateRangePicker). When set it
    // takes precedence over `days`; shape: {startDate, endDate, label, days} where
    // `days` is the derived inclusive day count. `null` = a preset (7/30/90) is active.
    const [range, setRangeState] = useState(null);
    const [orders, setOrders] = useState([]);
    const [secondary, setSecondary] = useState(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [intervalMs, setIntervalMsState] = useState(MANAGEMENT_REFRESH_DEFAULT_MS);

    // Refs mirror mutable state so timer callbacks read fresh values without
    // being torn down / recreated on every state change.
    const daysRef = useRef(days);
    const rangeRef = useRef(range);
    const scopeRef = useRef(scope);
    const intervalRef = useRef(intervalMs);
    const ordersInFlightRef = useRef(false);
    // A full refresh requested while another one was still running (e.g. the
    // user picks a new date range mid-poll). Replayed when the in-flight fetch
    // settles so the newly selected window always reaches the screen.
    const fullPendingRef = useRef(false);
    const fetchFullRef = useRef(null);
    const fullInFlightRef = useRef(false);
    // Signature of the orders array currently committed to state — lets the fast
    // poll skip setOrders (and the resulting context-wide re-render) on a no-op.
    const ordersSigRef = useRef(null);
    const hasLoadedRef = useRef(false);
    const fastTimerRef = useRef(null);
    const slowTimerRef = useRef(null);
    const appActiveRef = useRef(true);
    const mountedRef = useRef(true);

    useEffect(() => { daysRef.current = days; }, [days]);
    useEffect(() => { rangeRef.current = range; }, [range]);
    useEffect(() => { scopeRef.current = scope; }, [scope]);
    useEffect(() => { intervalRef.current = intervalMs; }, [intervalMs]);

    /**
     * Resolve the active date window for a fetch. A custom range (from the calendar
     * picker) wins over the preset day count and produces the same shape.
     * @returns {ReturnType<typeof getManagementDateRange>}
     */
    const resolveDateRange = useCallback(() => {
        const r = rangeRef.current;
        if (r && r.startDate && r.endDate) {
            return getManagementDateRange({startDate: r.startDate, endDate: r.endDate});
        }
        return getManagementDateRange(daysRef.current);
    }, []);

    /** Resolve (and cache) the management scope from persisted restaurant/place. */
    const resolveScope = useCallback(async () => {
        if (scopeRef.current) return scopeRef.current;
        const next = await getManagementScope();
        scopeRef.current = next;
        if (mountedRef.current) setScope(next);
        return next;
    }, []);

    /** FAST path: refresh only the orders list (drives stats + the orders screen). */
    const fetchOrdersOnly = useCallback(async () => {
        if (ordersInFlightRef.current || fullInFlightRef.current) return;
        if (!appActiveRef.current) return;
        ordersInFlightRef.current = true;
        try {
            const nextScope = await resolveScope();
            const dateRange = resolveDateRange();
            const next = await fetchManagementOrders(nextScope, dateRange);
            if (!mountedRef.current) return;
            const list = Array.isArray(next) ? next : [];
            // No-op guard: only push a new array reference (and re-render every
            // consumer) when the order data actually changed since the last commit.
            const sig = ordersSignature(list);
            if (sig !== ordersSigRef.current) {
                ordersSigRef.current = sig;
                setOrders(list);
            }
            setLastUpdatedAt(Date.now());
            setError('');
        } catch (err) {
            if (mountedRef.current) setError(err?.message || '');
        } finally {
            ordersInFlightRef.current = false;
        }
    }, [resolveScope, resolveDateRange]);

    /** SLOW/manual path: refresh the full dashboard aggregate (orders + secondary). */
    const fetchFull = useCallback(async ({silent = false} = {}) => {
        if (fullInFlightRef.current) {
            // A date-range (or scope) change that lands while the previous
            // aggregate is still in flight must NOT be swallowed — remember it
            // and replay it below, otherwise the dashboard keeps rendering the
            // old window until the next slow tick (>=60s) and the picker looks
            // like it does nothing.
            fullPendingRef.current = true;
            return;
        }
        fullInFlightRef.current = true;
        if (!silent && !hasLoadedRef.current && mountedRef.current) setLoading(true);
        try {
            const nextScope = await resolveScope();
            const dateRange = resolveDateRange();
            const next = await fetchManagementDashboardData(nextScope, dateRange);
            if (!mountedRef.current) return;
            const list = Array.isArray(next.orders) ? next.orders : [];
            // Same no-op guard as the fast path so the slow full refresh doesn't
            // hand React a brand-new orders array (and force a re-render) when the
            // orders themselves are unchanged.
            const sig = ordersSignature(list);
            if (sig !== ordersSigRef.current) {
                ordersSigRef.current = sig;
                setOrders(list);
            }
            setSecondary(next);
            setLastUpdatedAt(Date.now());
            setError('');
        } catch (err) {
            if (mountedRef.current) setError(err?.message || '');
        } finally {
            fullInFlightRef.current = false;
            hasLoadedRef.current = true;
            if (mountedRef.current) {
                setLoading(false);
                setRefreshing(false);
            }
            // Replay a window/scope change that arrived while this fetch was
            // still running (see the in-flight guard above).
            if (fullPendingRef.current && mountedRef.current) {
                fullPendingRef.current = false;
                fetchFullRef.current?.({silent: true});
            }
        }
    }, [resolveScope, resolveDateRange]);

    // Stable handle so the replay inside fetchFull can call the latest instance
    // without the callback having to depend on (and be recreated by) itself.
    fetchFullRef.current = fetchFull;

    const stopTimers = useCallback(() => {
        if (fastTimerRef.current) { globalThis.clearInterval(fastTimerRef.current); fastTimerRef.current = null; }
        if (slowTimerRef.current) { globalThis.clearInterval(slowTimerRef.current); slowTimerRef.current = null; }
    }, []);

    const startTimers = useCallback(() => {
        stopTimers();
        const ms = intervalRef.current;
        if (!ms || ms <= 0) return; // "Never" — polling disabled
        // For large date ranges (months / thousands of orders) the fast full-list
        // re-fetch is wasteful and provides no live benefit, so we rely on the slow
        // aggregate tick instead. The user-selected cadence is preserved for the
        // small ranges where "live" actually matters.
        // A custom range (rangeRef set → days=null) is treated like the >7-day
        // case: no fast orders-only tick, only the slow full-aggregate refresh.
        const fastPollEnabled = !rangeRef.current && (daysRef.current || 0) <= FAST_POLL_MAX_RANGE_DAYS;
        if (fastPollEnabled) {
            fastTimerRef.current = globalThis.setInterval(() => { fetchOrdersOnly(); }, ms);
        }
        slowTimerRef.current = globalThis.setInterval(() => {
            if (!appActiveRef.current) return;
            fetchFull({silent: true});
        }, Math.max(ms, SECONDARY_MIN_MS));
    }, [stopTimers, fetchOrdersOnly, fetchFull]);

    // Bootstrap: load the saved cadence, seed the full aggregate once, start polling.
    useEffect(() => {
        mountedRef.current = true;
        let cancelled = false;
        (async () => {
            const raw = await getSetting(MANAGEMENT_REFRESH_INTERVAL_KEY, null);
            const ms = sanitizeRefreshIntervalMs(raw);
            if (!cancelled && mountedRef.current) {
                setIntervalMsState(ms);
                intervalRef.current = ms;
            }
            await fetchFull({silent: false});
            if (!cancelled) startTimers();
        })();
        return () => {
            cancelled = true;
            mountedRef.current = false;
            stopTimers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pause polling in the background; resume + immediate fetch on foreground.
    useEffect(() => {
        const sub = AppState.addEventListener('change', (state) => {
            const active = state === 'active';
            const wasActive = appActiveRef.current;
            appActiveRef.current = active;
            if (active && !wasActive) {
                fetchOrdersOnly();
                startTimers();
            } else if (!active && wasActive) {
                stopTimers();
            }
        });
        return () => { sub?.remove?.(); };
    }, [fetchOrdersOnly, startTimers, stopTimers]);

    /** Change the analytics preset range (7/30/90); clears any custom range and refreshes. */
    const setDays = useCallback((value) => {
        const next = Number(value) || 30;
        setDaysState(next);
        daysRef.current = next;
        setRangeState(null);
        rangeRef.current = null;
        fetchFull({silent: true});
        startTimers();
    }, [fetchFull, startTimers]);

    /**
     * Apply an analytics date range from the calendar DateRangePicker. Accepts
     * either a preset (`{days}`) or an explicit custom window (`{startDate, endDate}`).
     * A custom range takes precedence over `days`; a preset clears any active range.
     * @param {{days?: (number|null), startDate?: (Date|string|null), endDate?: (Date|string|null), label?: string}} [selection]
     */
    const setRange = useCallback((selection = {}) => {
        const {days: presetDays = null, startDate = null, endDate = null, label = ''} = selection;
        if (startDate && endDate) {
            const next = {
                startDate,
                endDate,
                label,
                days: dayCountFromRange(startDate, endDate),
            };
            setRangeState(next);
            rangeRef.current = next;
        } else {
            const nextDays = Number(presetDays) || 30;
            setDaysState(nextDays);
            daysRef.current = nextDays;
            setRangeState(null);
            rangeRef.current = null;
        }
        fetchFull({silent: true});
        startTimers();
    }, [fetchFull, startTimers]);

    /** Change & persist the auto-refresh cadence; `0` disables polling. */
    const setIntervalMs = useCallback((ms) => {
        const next = sanitizeRefreshIntervalMs(ms);
        setIntervalMsState(next);
        intervalRef.current = next;
        setSetting(MANAGEMENT_REFRESH_INTERVAL_KEY, String(next)).catch(() => {});
        startTimers();
    }, [startTimers]);

    /** Manual (pull-to-refresh) full reload. */
    const refresh = useCallback(() => {
        if (mountedRef.current) setRefreshing(true);
        return fetchFull({silent: true});
    }, [fetchFull]);

    // Dashboard expects a single `data` aggregate; keep orders as the live field.
    const data = useMemo(() => ({...secondary, orders}), [secondary, orders]);

    const value = useMemo(() => ({
        scope, days, setDays, range, setRange,
        orders, data,
        loading, refreshing, error, lastUpdatedAt,
        intervalMs, setIntervalMs, refresh,
    }), [scope, days, setDays, range, setRange, orders, data, loading, refreshing, error, lastUpdatedAt, intervalMs, setIntervalMs, refresh]);

    return <ManagementDataContext.Provider value={value}>{children}</ManagementDataContext.Provider>;
};

export default ManagementDataContext;
